// Magnetic — a small wrapper that adds a cursor-magnet effect with
// spring-lag tracking, plus a click squish and hover scale. The "100px
// radius" zone is configurable but the brief specifies 100px.
//
// Behavior:
//   - On mousemove within the magnet radius, the element's translation
//     follows the cursor offset from its center, with a spring (stiffness
//     200, damping 18 per brief — elastic, not bouncy).
//   - On hover, the inner element scales to 1.05 (also via spring).
//   - On click, the inner element squishes to 0.95 and back (a 200ms
//     "tap" pulse).
//   - Outside the radius, the offset eases back to 0 with the same spring.
//
// The component is polymorphic: pass `as="button"` / `as="a"` / `as="div"`
// to render the right element. `onClick`, `href`, `type`, etc. are
// forwarded. The wrapper element is what the user clicks — that's what
// gets the magnet. The "inner" element (rendered as a child <motion.span>)
// gets the hover scale and click squish. This two-element pattern keeps
// the magnet transform isolated from the scale transform.
//
// Reduced motion: when prefers-reduced-motion is on, we disable the
// magnetic follow + the hover scale spring; the click squish still
// works (it's a quick 200ms) so the user gets a tactile confirmation.

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type MouseEvent,
  type CSSProperties,
  type ElementType,
} from 'react';
import {
  motion,
  useMotionValue,
  useSpring,
  useReducedMotion,
  type HTMLMotionProps,
} from 'motion/react';

// Spring config per the brief — stiffness 200, damping 18.
const MAGNETIC_SPRING = { stiffness: 200, damping: 18, mass: 0.8 } as const;
// Scale spring — slightly snappier so the hover scale is responsive.
const SCALE_SPRING = { stiffness: 200, damping: 18, mass: 0.7 } as const;
// Click squish — quick, no spring needed.
const SQUISH_KEYFRAMES = [1, 0.95, 1] as const;
const SQUISH_DURATION = 0.24; // seconds

// Default magnetic radius in pixels.
export const MAGNETIC_RADIUS_PX = 100;
// How strongly the element follows the cursor near the center.
// At dist=0, the element is pulled to ~strength*cursorOffset.
// We keep this under 1 so the element never escapes its slot.
const MAGNETIC_STRENGTH = 0.35;

export interface MagneticProps {
  /** Element type to render. Defaults to 'button'. */
  as?: ElementType;
  /** Forwarded children. */
  children: ReactNode;
  /** Magnetic radius in pixels. Defaults to 100 per brief. */
  radius?: number;
  /** Strength factor (0..1). Defaults to 0.35. */
  strength?: number;
  /** Disable the magnet entirely. Children still get hover scale. */
  disabled?: boolean;
  /** Class applied to the outer (magnetic) wrapper. */
  className?: string;
  /** Style applied to the outer wrapper. */
  style?: CSSProperties;
  /** Inner className (the element that scales on hover). */
  innerClassName?: string;
  /** Inner style (for the element that scales on hover). */
  innerStyle?: CSSProperties;
  /** Click handler. */
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /** ARIA label forwarded to the wrapper. */
  ariaLabel?: string;
  /** Native title attribute (tooltip). Forwarded to the wrapper. */
  title?: string;
  /** HTML id forwarded to the wrapper. */
  id?: string;
  /** Type for buttons. */
  type?: 'button' | 'submit' | 'reset';
  /** Href for anchors. */
  href?: string;
  /** Target for anchors. */
  target?: string;
  /** Rel for anchors. */
  rel?: string;
  /** ARIA role — forwarded to the wrapper (defaults to undefined). */
  role?: string;
  /** ARIA-selected — for tablist/tab patterns. */
  'aria-selected'?: boolean;
  /** ARIA-controls — for tablist/tab patterns. */
  'aria-controls'?: string;
  /** tabIndex override — useful for roving tabindex on tabs. */
  tabIndex?: number;
}

/**
 * Wraps interactive elements with cursor-magnet, hover-spring-scale, and
 * click-squish motion. The outer element translates; the inner element
 * scales. Use a single <Magnetic> for each interactive target.
 */
export function Magnetic({
  as: As = 'button',
  children,
  radius = MAGNETIC_RADIUS_PX,
  strength = MAGNETIC_STRENGTH,
  disabled = false,
  className,
  style,
  innerClassName,
  innerStyle,
  onClick,
  ariaLabel,
  title,
  id,
  type = 'button',
  href,
  target,
  rel,
  role,
  'aria-selected': ariaSelected,
  'aria-controls': ariaControls,
  tabIndex,
}: MagneticProps) {
  const wrapperRef = useRef<HTMLElement | null>(null);
  const reducedMotion = useReducedMotion();
  const [hovered, setHovered] = useState(false);

  // Motion values: the cursor offset relative to the element center.
  // The spring follows these toward 0 when the cursor leaves.
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  // Springs (configurable). When reduced motion is on, set stiffness to
  // a high value so the spring is effectively instant (no floaty feel).
  const sx = useSpring(mx, reducedMotion ? { stiffness: 1000, damping: 100 } : MAGNETIC_SPRING);
  const sy = useSpring(my, reducedMotion ? { stiffness: 1000, damping: 100 } : MAGNETIC_SPRING);

  // Scale: 1 baseline, 1.05 on hover, [1, 0.95, 1] on tap.
  const scale = useSpring(useMotionValue(1), SCALE_SPRING);
  const [tapKey, setTapKey] = useState(0);

  useEffect(() => {
    scale.set(hovered ? 1.05 : 1);
  }, [hovered, scale]);

  // Mousemove — track the cursor within the magnet zone.
  const handleMove = (e: MouseEvent<HTMLElement>) => {
    if (disabled || reducedMotion) return;
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const dist = Math.hypot(dx, dy);
    if (dist < radius) {
      // Power falls off linearly: at the center the element follows
      // the cursor by `strength`; at the edge, 0.
      const power = 1 - dist / radius;
      mx.set(dx * strength * power);
      my.set(dy * strength * power);
    } else {
      mx.set(0);
      my.set(0);
    }
  };

  const handleLeave = () => {
    mx.set(0);
    my.set(0);
    setHovered(false);
  };

  const handleEnter = () => {
    setHovered(true);
  };

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    // Trigger a squish — bumping the key forces the keyframes to replay.
    setTapKey((k) => k + 1);
    onClick?.(e);
  };

  // Build the props shared by all variants. The outer element is the
  // magnet — it gets x/y translations. The inner span gets the scale.
  const outerStyle: CSSProperties = {
    ...style,
    // Cast: motion's MotionValue<number> flows through x/y at runtime;
    // TS sees only the legacy CSS string|number union. This pattern
    // mirrors how CursorFollower passes motion values to its div.
    x: sx as unknown as number,
    y: sy as unknown as number,
    willChange: 'transform',
  };

  const innerCombinedStyle: CSSProperties = {
    ...innerStyle,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    willChange: 'transform',
  };

  // Inner content: hover scale spring + click squish.
  const innerMotion = (
    <motion.span
      className={innerClassName}
      style={innerCombinedStyle}
      animate={{ scale: hovered ? 1.05 : 1 }}
      transition={{ type: 'spring', ...SCALE_SPRING }}
    >
      <motion.span
        // Squish — replay on each tap. Bump the key to restart the
        // animation cleanly.
        key={tapKey}
        initial={{ scale: 1 }}
        animate={{ scale: [1, 0.95, 1] as unknown as number[] }}
        transition={{
          duration: SQUISH_DURATION,
          ease: [0.34, 1.56, 0.64, 1] as unknown as number[],
        }}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {children}
      </motion.span>
    </motion.span>
  );

  // Common props shared across the rendered element variants.
  const sharedProps = {
    ref: wrapperRef as unknown as React.Ref<HTMLElement>,
    className,
    style: outerStyle,
    onMouseMove: handleMove as unknown as React.MouseEventHandler<HTMLElement>,
    onMouseEnter: handleEnter as unknown as React.MouseEventHandler<HTMLElement>,
    onMouseLeave: handleLeave as unknown as React.MouseEventHandler<HTMLElement>,
    onClick: handleClick as unknown as React.MouseEventHandler<HTMLElement>,
    'aria-label': ariaLabel,
    title,
    id,
    role,
    'aria-selected': ariaSelected,
    'aria-controls': ariaControls,
    tabIndex,
  } as const;

  // Render the right element type. We use motion components so the
  // x/y motion values flow through. The "as" prop is a string so we
  // switch on it (avoids dynamic-component friction with TypeScript).
  if (As === 'a') {
    return (
      <motion.a
        {...(sharedProps as unknown as HTMLMotionProps<'a'>)}
        href={href}
        target={target}
        rel={rel}
      >
        {innerMotion}
      </motion.a>
    );
  }
  if (As === 'div') {
    return (
      <motion.div {...(sharedProps as unknown as HTMLMotionProps<'div'>)}>
        {innerMotion}
      </motion.div>
    );
  }
  if (As === 'span') {
    return (
      <motion.span {...(sharedProps as unknown as HTMLMotionProps<'span'>)}>
        {innerMotion}
      </motion.span>
    );
  }
  // Default: button.
  return (
    <motion.button
      {...(sharedProps as unknown as HTMLMotionProps<'button'>)}
      type={type}
    >
      {innerMotion}
    </motion.button>
  );
}

export default Magnetic;
